import { faDollarSign } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { FormattedMessage } from 'react-intl/dist';
import MaskedInput from 'react-text-mask';
import styled from 'styled-components';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { ConditionalRendering } from '../conditional-rendering';
import Tooltip from '../tooltip/Tooltip';

const StyledIcon = styled.span`
    height: 40px;
    width: 30px;
    border-radius: 5px 0 0 5px;
    align-items: center;
    justify-content: center;
    display: flex;
    flex-wrap: nowrap;
    z-index: 1;
    margin-right: -3px;
    background-color: ${({ theme }) => theme.gray9};
    color: ${({ theme }) => theme.gray3};
    border: ${({ theme }) => `1px solid ${theme.gray7}`};
`;

const StyledCurrencyInputContainer = styled.div`
    min-width: 10rem;
`;

const StyledCurrencyInput = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
`;

const defaultMaskOptions = {
    prefix: '$',
    suffix: '',
    includeThousandsSeparator: true,
    thousandsSeparatorSymbol: ',',
    allowDecimal: true,
    decimalSymbol: '.',
    decimalLimit: 2, // how many digits allowed after the decimal
    integerLimit: 6, // limit length of integer numbers
    allowNegative: false,
    allowLeadingZeroes: false,
};

const CurrencyInput = props => {
    const {
        maskOptions,
        label: { wrapperClasses, labelClasses, showRequired, labelText, isInputRequired },
        input,
        showIcon,
        input: { id, name, value, inputclasses, fieldArrayIndex = undefined, fieldArray = [] },
        actions: { handleChange },
    } = props;
    const currencyMask = createNumberMask({
        ...defaultMaskOptions,
        ...maskOptions,
    });
    const handleValueChange = e => {
        handleChange({ target: { name, value: e.target.value } }, fieldArray, fieldArrayIndex);
    };

    return (
        <Fragment>
            <StyledCurrencyInputContainer
                className={`grid-row gi-input gi-component ${wrapperClasses && wrapperClasses.join(' ')}`}
            >
                <ConditionalRendering isTrue={labelText}>
                    <label
                        htmlFor={name}
                        className={`gi-input__label ${labelClasses && labelClasses.join(' ')} required-${showRequired}`}
                        aria-label={isInputRequired}
                    >
                        <FormattedMessage id={labelText} />
                        <Tooltip
                            id="anonymous.pre_eligibility.pre_screen.content.income.field1.1"
                            className="tooltip__display--inline"
                            content="anonymous.pre_eligibility.pre_screen.content.income.field1.1.tooltip"
                            index="1"
                            place="top"
                            contentClassName=""
                        />
                    </label>
                </ConditionalRendering>

                <StyledCurrencyInput>
                    <ConditionalRendering isTrue={showIcon}>
                        <StyledIcon>
                            <FontAwesomeIcon icon={faDollarSign} className="gi-input__icon" />
                        </StyledIcon>
                    </ConditionalRendering>

                    <MaskedInput
                        id={id}
                        data-testid={id}
                        data-automation-id={id}
                        name={name}
                        className={`gi-input__input ${inputclasses && inputclasses.join(' ')}`}
                        mask={currencyMask}
                        {...input}
                        value={value}
                        onChange={handleValueChange}
                    />
                </StyledCurrencyInput>
            </StyledCurrencyInputContainer>
        </Fragment>
    );
};

CurrencyInput.defaultProps = {
    inputMode: 'numeric',
    maskOptions: {},
};

CurrencyInput.propTypes = {
    inputmode: PropTypes.string,
    maskOptions: PropTypes.shape({
        prefix: PropTypes.string,
        suffix: PropTypes.string,
        includeThousandsSeparator: PropTypes.bool,
        thousandsSeparatorSymbol: PropTypes.string,
        allowDecimal: PropTypes.bool,
        decimalSymbol: PropTypes.string,
        decimalLimit: PropTypes.string,
        requireDecimal: PropTypes.bool,
        allowNegative: PropTypes.bool,
        allowLeadingZeroes: PropTypes.bool,
        integerLimit: PropTypes.number,
    }),
};

export default CurrencyInput;
